import type { System, World } from '../../engine/ecs';
import type { Rng } from '../../engine/rng';
import { Tile, tileAtPixel, type TileMap } from '../../world/tilemap';
import type { EncounterEntry, MapDef } from '../../world/maps/valleDelleFaglie';
import { createLumine, type LumineInstance } from '../lumine';
import { PlayerControlled, Position } from '../components';

/**
 * Incontri selvatici: ogni "passo" (TILE di distanza percorsa) su erba alta
 * o su un'anomalia tira un dado. Le anomalie ospitano Lumini più rari e forti.
 */

const STEP_LENGTH = 28; // px percorsi che contano come un passo

export type EncounterCallback = (wild: LumineInstance, source: 'tallGrass' | 'anomaly') => void;

export function pickEncounter(entries: EncounterEntry[], rng: Rng): LumineInstance {
  const total = entries.reduce((sum, e) => sum + e.weight, 0);
  let roll = rng.next() * total;
  for (const entry of entries) {
    roll -= entry.weight;
    if (roll <= 0) {
      const level = rng.int(entry.minLevel, entry.maxLevel);
      return createLumine(entry.speciesId, level, rng);
    }
  }
  const last = entries[entries.length - 1]!;
  return createLumine(last.speciesId, rng.int(last.minLevel, last.maxLevel), rng);
}

export class EncounterSystem implements System {
  readonly name = 'encounter';
  /** Tregua dopo ogni incontro per non incatenare battaglie. */
  private cooldown = 0;

  constructor(
    private mapDef: MapDef,
    private map: TileMap,
    private rng: Rng,
    private enabled: () => boolean,
    private onEncounter: EncounterCallback,
  ) {}

  update(world: World, dt: number): void {
    if (!this.enabled()) return;
    if (this.cooldown > 0) {
      this.cooldown -= dt;
      return;
    }
    for (const entity of world.query(Position, PlayerControlled)) {
      const pos = world.require(entity, Position);
      const control = world.require(entity, PlayerControlled);
      if (control.encounterMeter < STEP_LENGTH) continue;
      control.encounterMeter = 0;

      const tile = tileAtPixel(this.map, pos.x, pos.y - 4);
      let source: 'tallGrass' | 'anomaly' | null = null;
      if (tile === Tile.TallGrass) source = 'tallGrass';
      else if (tile === Tile.Anomaly) source = 'anomaly';
      if (!source) continue;

      if (this.rng.chance(this.mapDef.encounterRate[source])) {
        this.cooldown = 1.2;
        const wild = pickEncounter(this.mapDef.encounters[source], this.rng);
        this.onEncounter(wild, source);
      }
    }
  }
}
